package com.wintermar.inspection.feature.presentation.inspection.complete.widget

import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.HorizontalDivider
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.wintermar.inspection.core.ui.theme.AppDimension
import com.wintermar.inspection.core.ui.theme.LocalAppTheme
import com.wintermar.inspection.core.ui.theme.color.model.ThemeColor
import com.wintermar.inspection.feature.domain.model.answer.SummaryAnswerModel
import com.wintermar.inspection.feature.domain.model.inspection.InspectionModel
import com.wintermar.inspection.feature.domain.model.template.TemplateOptionModel
import com.wintermar.inspection.feature.presentation.inspection.widget.AnswerSummaryContainer
import com.wintermar.inspection.feature.presentation.inspection.widget.AnswerSummaryHeader
import com.wintermar.inspection.feature.presentation.inspection.widget.AnswerSummaryView
import com.wintermar.inspection.feature.presentation.inspection.widget.InspectionHeaderInfo
import com.wintermar.inspection.feature.presentation.inspection.widget.InspectionInfo
import com.wintermar.inspection.util.Constant
import inspection.composeapp.generated.resources.Res
import inspection.composeapp.generated.resources.flagged_items
import org.jetbrains.compose.resources.stringResource

@Composable
fun InspectionCompleteView(
    showOverviewBorder: Boolean,
    data: InspectionModel,
    flaggedItems: List<SummaryAnswerModel>,
    templateOptions: List<TemplateOptionModel>,
    listState: LazyListState,
    paddingValues: PaddingValues
) {
    val dimension: AppDimension = LocalAppTheme.current.dimension
    val color: ThemeColor = LocalAppTheme.current.color

    LazyColumn(state = listState) {
        item(key = Constant.OVERVIEW_KEY) {
            if (showOverviewBorder) Spacer(modifier = Modifier.height(dimension.spacing.space5))
            Column(
                modifier = if (showOverviewBorder) Modifier
                    .padding(
                        bottom = dimension.spacing.space5
                    ).border(
                        width = 1.dp,
                        color = color.border.subtle,
                        shape = RoundedCornerShape(dimension.radius.md)
                    )
                else Modifier
            ) {
                InspectionHeaderInfo(
                    totalAnswer = data.totalAnswer,
                    totalFlaggedAnswer = flaggedItems.size,
                    totalQuestion = data.totalQuestion,
                    templateName = data.templateName,
                    lastUpdateIsoUtc0 = data.lastUpdated,
                    status = data.status,
                    withTopRadius = showOverviewBorder
                )
                InspectionInfo(
                    vesselName = data.vesselName,
                    locationName = data.template.getLocation(),
                    conducted = data.conducted,
                    lastUpdated = data.lastUpdated,
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = dimension.spacing.space4)
                )
            }
        }

        if (flaggedItems.isNotEmpty()) {
            item(key = Constant.FLAGGED_ITEM_KEY) {
                AnswerSummaryView(
                    title = stringResource(Res.string.flagged_items),
                    items = flaggedItems,
                    templateOptions = templateOptions,
                    horizontalPadding = dimension.spacing.space4
                )
                Spacer(modifier = Modifier.height(dimension.spacing.space3))
            }

            flaggedItems.groupBy { it.pageTitle }.forEach { (page, sections) ->
                item(key = page) {
                    AnswerSummaryContainer(
                        clickable = false,
                        horizontalPadding = dimension.spacing.space0
                    ) {
                        Column {
                            AnswerSummaryHeader(
                                title = page,
                                hideExpandIcon = true,
                                totalItems = sections.size,
                                verticalPadding = dimension.spacing.space2,
                                horizontalPadding = dimension.spacing.space4,
                                expand = true
                            )
                            HorizontalDivider(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(horizontal = dimension.spacing.space4),
                                color = color.border.default
                            )
                            sections.groupBy { it.sectionTitle }
                                .forEach { (section, questions) ->
                                    AnswerSummaryView(
                                        title = section,
                                        items = questions,
                                        templateOptions = templateOptions,
                                        removeClippedCorners = true,
                                        horizontalPadding = dimension.spacing.space4,
                                        contentVerticalPadding = dimension.spacing.space2,
                                        contentHorizontalPadding = dimension.spacing.space0
                                    )
                                    HorizontalDivider(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .padding(horizontal = dimension.spacing.space4),
                                        color = color.border.default
                                    )
                                }
                        }
                    }
                    Spacer(modifier = Modifier.height(dimension.spacing.space3))
                }
            }
        }

        item {
            Spacer(modifier = Modifier.height(paddingValues.calculateBottomPadding()))
        }
    }
}
